import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  addEdge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  useReactFlow
} from 'reactflow';
import 'reactflow/dist/style.css';
import RoleNode from './react-role-card';
import { Button, Divider, Box, Card, CardContent, Typography } from '@mui/material';
import { getRequest, postRequest, deleteRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';
import CreateCardDialog from './role-dialog';
import { enqueueSnackbar } from 'notistack';
import { handleErrorMessages } from 'src/components/risk-management/risk-register/feching';
import CustomEdge from './custom-edges';
import { getRandomColor } from 'src/_mock';

const initialEdges = [];

export function RolesHierarchy() {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  const reactFlowInstance = useReactFlow(); 

  const getNodesData = async () => {
    try {
      const response = await getRequest(endpoints.organizational_hierarchy.crud_heirarchy);
      const data = response || [];
      if (data && data.length > 0) {
        const nodesFromAPI = data.map((node) => ({
          id: node.id.toString(),
          type: 'role',
          position: {
            x: parseFloat(node.position_x) || Math.random() * 600,
            y: parseFloat(node.position_y) || Math.random() * 400,
          },
          data: {
            id: node.id,
            roleName: node.name,
            isUsed: node.used_in_project || node.used_in_employee,
            color: getRandomColor(),
            users: node.user ? [node.user] : [],
            onDelete: handleRemoveNode,
            onEdit: handleEditRole,
            onRefresh: handleRefresh,
          },
        }));

        setNodes(nodesFromAPI);
      }
    } catch (error) {
      console.error('Error fetching nodes data:', error);
    }
  };

  const handleRemoveNode = async (id) => {
    try {
      if (!id) return;
      const response = await deleteRequest(`${endpoints.organizational_hierarchy.crud_heirarchy}/${id}`);
      if (response) {
        setNodes((prev) => prev.filter((node) => node.id !== id.toString()));
        setEdges((prev) =>
          prev.filter((edge) => edge.source !== id.toString() && edge.target !== id.toString())
        );
        getNodesData();
        enqueueSnackbar(response?.metadata?.message || 'Role deleted successfully', {
          variant: 'success',
        });
      } else {
        handleErrorMessages(response?.metadata?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error deleting role', {
        variant: 'error',
      });
    }
  };

  const getParentChildNodes = async () => {
    try {
      const response = await getRequest(endpoints.organizational_hierarchy.parent_child_heirarchy);
      const data = response?.parent_child_mappings || [];
      if (data.length > 0) {
        const allNodes = new Map();
        const newEdges = [];

        data.forEach((relationship) => {
          const parentNode = relationship?.parent;
          if (!parentNode) return;

          if (!allNodes.has(parentNode.id.toString())) {
            allNodes.set(parentNode.id.toString(), {
              id: parentNode.id.toString(),
              type: 'role',
              position: {
                x: parseFloat(parentNode.position_x) || 100,
                y: parseFloat(parentNode.position_y) || 100,
              },
              data: {
                id: parentNode.id,
                roleName: parentNode.name,
                isUsed: parentNode.used_in_project || parentNode.used_in_employee,
                color: getRandomColor(),
                users: parentNode.users ? [parentNode.users] : [],
                onDelete: handleRemoveNode,
                onEdit: handleEditRole,
                onRefresh: handleRefresh,
              },
            });
          }

          relationship.children.forEach((childNode) => {
            if (!childNode) return;

            if (!allNodes.has(childNode.id.toString())) {
              allNodes.set(childNode.id.toString(), {
                id: childNode.id.toString(),
                type: 'role',
                position: {
                  x: parseFloat(childNode.position_x) || 100,
                  y: parseFloat(childNode.position_y) || 300,
                },
                data: {
                  id: childNode.id,
                  roleName: childNode.name,
                  isUsed: childNode.used_in_project || childNode.used_in_employee,
                  color: getRandomColor(),
                  users: childNode.users ? [childNode.users] : [],
                  onDelete: handleRemoveNode,
                  onEdit: handleEditRole,
                  onRefresh: handleRefresh,
                },
              });
            }

            newEdges.push({
              id: `e-${parentNode.id}-${childNode.id}`,
              source: parentNode.id.toString(),
              target: childNode.id.toString(),
              type: 'removable',
              data: { onDelete: handleRemoveEdge },
            });
          });
        });

        setNodes(Array.from(allNodes.values()));
        setEdges(newEdges);
      }
    } catch (error) {
      console.error('Error fetching parent-child data:', error);
      enqueueSnackbar('Error loading organization structure', { variant: 'error' });
    }
  };

const onConnect = useCallback(
  async (params) => {
    const sourceNode = nodes.find((node) => node.id === params.source);
    const targetNode = nodes.find((node) => node.id === params.target);

    if (sourceNode && targetNode) {
      const hierarchyData = [
        {
          source: sourceNode.data.id,
          target: targetNode.data.id,
        },
      ];

      try {
        const response = await postRequest(
          endpoints.organizational_hierarchy.parent_child_heirarchy_attach_nodes,
          { hierarchy: hierarchyData }
        );

        if (response?.metadata) {
          const newEdge = addEdge(
            {
              ...params,
              type: 'removable',
              data: { onDelete: handleRemoveEdge },
            },
            edges
          );
          setEdges(newEdge);
          enqueueSnackbar({ variant: 'success', message: response?.metadata?.message });
        } else {
          enqueueSnackbar({ variant: 'error', message: response?.message  });
        }
      } catch (err) {
        console.error('Failed to attach hierarchy:', err);
        enqueueSnackbar({ variant: 'error', message: err?.response?.data?.message });
      }
    }
  },
  [nodes, edges, setEdges]
);


  const handleRemoveEdge = async (edgeId, sourceId, targetId) => {
    setEdges((curr) => curr.filter((e) => e.id !== edgeId));
    const hierarchyData = [
      {
        source: Number(sourceId),
        target: Number(targetId),
      },
    ];

    try {
      await postRequest(endpoints.organizational_hierarchy.detach_nodeds, {
        hierarchy: hierarchyData,
      });
      enqueueSnackbar({ variant: 'success', message: 'Hierarchy detached successfully' });
      getNodesData();
      getParentChildNodes();
    } catch (err) {
      console.error('Failed to detach hierarchy', err);
    }
  };

  const postHierarchyData = async (hierarchyData) => {
    try {
      const response = await postRequest(
        endpoints.organizational_hierarchy.parent_child_heirarchy_attach_nodes,
        { hierarchy: hierarchyData }
      );
      if (response) {
        enqueueSnackbar({ variant: 'success', message: response?.metadata?.message });
      }
    } catch (error) {
      console.error('Error updating hierarchy:', error);
    }
  };

  const handleAddRole = (roleData) => {
    const tempId = `temp-${Date.now()}`;
    const color = getRandomColor();

    const { x: offsetX, y: offsetY, zoom } = reactFlowInstance.getViewport();
    const centerX = (window.innerWidth / 2 - offsetX) / zoom;
    const centerY = (window.innerHeight / 2 - offsetY) / zoom;

    const newNode = {
      id: tempId,
      type: 'role',
      position: { x: centerX, y: centerY },
      data: {
        ...roleData,
        color,
        onDelete: handleRemoveNode,
        onEdit: handleEditRole,
        onRefresh: handleRefresh,
      },
    };
    setNodes((nds) => [...nds, newNode]);
  };

  const onNodeDragStop = (event, node) => {
    console.log('Node dragged:', node.id, node.position);
  };

  const handleSaveChanges = async () => {
    const positionsData = nodes.map((node) => ({
      id: node.data.id,
      position_x: node.position.x,
      position_y: node.position.y,
    }));
    try {
      const response = await postRequest(endpoints.organizational_hierarchy.nodes_positions, {
        positions: positionsData,
      });
      if (response) {
        enqueueSnackbar(response?.metadata?.message || 'Positions saved successfully', {
          variant: 'success',
        });
      }
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || 'Error saving positions', {
        variant: 'error',
      });
    }
  };

  const handleEditRole = (roleData) => {
    setCurrentItem(roleData);
    setDialogOpen(true);
  };

  const handleRefresh = () => {
    getNodesData();
    getParentChildNodes();
  };

  const nodeTypes = useMemo(() => ({ role: (props) => <RoleNode {...props} /> }), []);
  const edgeTypes = { removable: CustomEdge };

  useEffect(() => {
    getNodesData();
    getParentChildNodes();
  }, []);

  return (
    <Box sx={{ backgroundColor: '#eee', minHeight: '100vh', padding: '20px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <h2>Organizational Structure Hierarchy</h2>
        <Box>
          <Button variant="contained" className="dark-button" onClick={handleSaveChanges} sx={{ mr: 1 }}>
            Save Changes
          </Button>
          <Button
            variant="contained"
            className="dark-button"
            onClick={() => {
              setCurrentItem(null);
              setDialogOpen(true);
            }}
          >
            + Add New
          </Button>
        </Box>
      </Box>

      <Card sx={{ backgroundColor: '#FFFBEA', borderLeft: '4px solid #FFD700', mb: 2, mx: 2 }}>
        <CardContent>
          <Typography variant="body2" sx={{ fontSize: '14px', color: '#444' }}>
            <strong>Note:</strong> Use this interface to build your Organizational Structure by adding positions and
            relationships. Click “Save Changes” to preserve your structure.
          </Typography>
        </CardContent>
      </Card>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ height: '100vh', backgroundColor: '#fff', borderRadius: '8px' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeDragStop={onNodeDragStop}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          nodesDraggable
        >
          <Controls />
          <Background variant="" gap={12} size={1} />
        </ReactFlow>
      </Box>

      <CreateCardDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setCurrentItem(null);
        }}
        currentItem={currentItem}
        reload={getNodesData}
        onAdd={handleAddRole}
      />
    </Box>
  );
}
